/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import type { TeamSeasonStats } from '@sportninja/common/features/playerFinder/aggregateSeasonStats'
import { getImageThumbnailId } from '@sportninja/common/utils/utils'
import React from 'react'
import { font, weightBold, weightRegular } from 'src/components/css'

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

const TEAM_COL_WIDTH = 150
const STAT_COL_WIDTH = 56
const ROW_HEIGHT = 56
const HEADER_HEIGHT = 36

export interface TeamStatsTableProps {
  teams: TeamSeasonStats[]
}

/**
 * Union of every stat abbr across all teams, in first-seen order, so each row
 * shares one aligned set of columns even when teams report different stats.
 */
const columnOrder = (teams: TeamSeasonStats[]): string[] => {
  const order: string[] = []
  const seen = new Set<string>()
  for (const team of teams) {
    for (const cell of team.cells) {
      if (!seen.has(cell.abbr)) {
        seen.add(cell.abbr)
        order.push(cell.abbr)
      }
    }
  }
  return order
}

/**
 * Per-team Statistics table, mirroring mobile's TeamStatsTable: a fixed team
 * column (logo + name + season) that stays put, and stat columns that scroll
 * horizontally together. Read-only — nothing here is clickable.
 */
const TeamStatsTable: React.FC<TeamStatsTableProps> = ({ teams }) => {
  'use memo'
  const cols = columnOrder(teams)

  const valueFor = (team: TeamSeasonStats, abbr: string) =>
    team.cells.find((c) => c.abbr === abbr)?.value ?? '-'

  const resolveLogo = (team: TeamSeasonStats) =>
    getImageThumbnailId(team.team) ||
    (team.logoFallback ? getImageThumbnailId(team.logoFallback) : '') ||
    TEAM_PLACEHOLDER

  return (
    <div css={tableStyle}>
      {/* Fixed team column — never scrolls, so the row a stat belongs to stays
          identifiable while the stat columns move. */}
      <div css={teamColumnStyle}>
        <div css={[headerRowStyle, teamHeaderCellStyle]}>
          <span css={headerTextStyle}>TEAM</span>
        </div>
        {teams.map((team, i) => (
          <div
            key={team.teamId ?? i}
            css={[teamCellStyle, i > 0 && rowDividerStyle]}
          >
            <div css={teamNameRowStyle}>
              <img
                css={logoStyle}
                src={resolveLogo(team)}
                alt=''
                onError={(e) => {
                  // A dead logo URL would otherwise show a broken-image glyph.
                  e.currentTarget.src = TEAM_PLACEHOLDER
                }}
              />
              <span css={teamNameStyle}>{team.teamName}</span>
            </div>
            {!!team.seasonName && (
              <span css={seasonStyle}>{team.seasonName}</span>
            )}
          </div>
        ))}
      </div>

      {/* Scrollable stat columns */}
      <div css={statsScrollStyle}>
        <div css={statsContentStyle}>
          <div css={headerRowStyle}>
            {cols.map((abbr) => (
              <div key={abbr} css={statColStyle}>
                <span css={headerTextStyle}>{abbr}</span>
              </div>
            ))}
          </div>
          {teams.map((team, i) => (
            <div
              key={team.teamId ?? i}
              css={[statRowStyle, i > 0 && rowDividerStyle]}
            >
              {cols.map((abbr) => (
                <div key={abbr} css={statColStyle}>
                  <span css={statValueStyle}>{valueFor(team, abbr)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default TeamStatsTable

const tableStyle = css`
  display: flex;
  border: 1px solid ${colors.SOFT_STEEL};
  border-radius: 8px;
  background-color: ${colors.SECONDARY_400};
  overflow: hidden;
`

const rowDividerStyle = css`
  border-top: 1px solid ${colors.SOFT_STEEL};
`

const teamColumnStyle = css`
  width: ${TEAM_COL_WIDTH}px;
  flex: 0 0 ${TEAM_COL_WIDTH}px;
  border-right: 1px solid ${colors.SOFT_STEEL};
`

const headerRowStyle = css`
  display: flex;
  height: ${HEADER_HEIGHT}px;
  align-items: center;
  border-bottom: 1px solid ${colors.SOFT_STEEL};
`

const teamHeaderCellStyle = css`
  padding-left: 12px;
`

const headerTextStyle = css`
  ${font.body}
  ${weightBold}
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_50};
  white-space: nowrap;
`

const teamCellStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: ${ROW_HEIGHT}px;
  padding: 0 8px 0 12px;
  min-width: 0;
`

const teamNameRowStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`

const logoStyle = css`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  background-color: ${colors.NEUTRAL_0};
  flex: 0 0 auto;
`

const teamNameStyle = css`
  ${font.body}
  ${weightBold}
  font-size: 14px;
  line-height: 18px;
  color: ${colors.WHITE};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const seasonStyle = css`
  ${font.body}
  ${weightRegular}
  font-size: 12px;
  line-height: 16px;
  margin-top: 2px;
  color: ${colors.NEUTRAL_50};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const statsScrollStyle = css`
  overflow-x: auto;
  flex: 1;
  min-width: 0;
`

/* Sizes to the full stat-column width rather than the scroll viewport, so the
   header underline and row dividers span every column instead of stopping at
   the visible edge once the table is scrolled. */
const statsContentStyle = css`
  width: max-content;
  min-width: 100%;
`

const statColStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${STAT_COL_WIDTH}px;
  flex: 0 0 ${STAT_COL_WIDTH}px;
`

const statRowStyle = css`
  display: flex;
  height: ${ROW_HEIGHT}px;
  align-items: center;
`

const statValueStyle = css`
  ${font.title}
  ${weightBold}
  font-size: 15px;
  line-height: 19px;
  color: ${colors.WHITE};
`
